import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  useCreateMasteryWorkflowMutation,
  useUpdateMasteryWorkflowMutation,
  useGetMasteryWorkflowByIdQuery,
} from "../../../../Api/attoDeskApi";
import { useNotifier } from "../../../../Core/Notifier";

import { LegacyTokens } from "../../../../theme/b13Tokens";
import WorkflowModal from "../shared/WorkflowModal";
import WorkflowEditorBody, {
  EditorPhase, makeEmptyPhase, normalisePhase, accuracyValidation,
} from "../shared/WorkflowEditorBody";
import ObjectiveChooserDialog from "../shared/ObjectiveChooserDialog";
import { objectiveByKey, WorkflowObjective } from "../shared/workflowObjectives";
import { createStandardWorkflowForObjective } from "../shared/standardWorkflowFactory";

// Batch 7 — mastery workflow editor.
//
// This route now renders the shared WorkflowModal + WorkflowEditorBody
// so the editing surface is byte-identical whether the user opens it
// from Settings or from the program editor's inline "+ Create new
// workflow" affordance.
//
// Removed (per §B / §C / §7.2 / §7.3 / §7.4):
//   - Save Draft button
//   - "Cannot save & activate: …" floating error above the footer
//   - Hardcoded 5-option phaseName <Select> at old lines 504-514 —
//     replaced by the free-text Label input inside EditPhaseModal
//   - Status dropdown (draft/active/archived) — one SAVE, no draft state
//   - The duplicate Workflow-Flow / Phases panels
// Kept (moved into Advanced-settings collapse on each phase card):
//   - Description, Starting/Final/Active toggles, Required Prompt
//     Level, Supervisor Approval, Next Phase + Criteria, Move Back
//     rule, live SUMMARY, reorder/duplicate/delete actions

const STANDARD_PHASES: Omit<EditorPhase, "orderNumber" | "id">[] = [
  { phaseName: "Baseline",       colour: LegacyTokens.neutralDot, description: "Assess without prompting. Starting phase.",     entryRule: "New target added",   advancementRule: "Move to Acquisition after 1 baseline session",  regressionRule: "",                                          transitionsTo: "Acquisition", masteredTargetStatus: "", notMasteredTransitionsTo: "",    icon: "AssessmentOutlined", minPercentCorrect: 0,   acrossSessions: 1, minTrialsPerSession: 3, promptRequirement: "any",              manualApprovalRequired: false, isStarting: true,  isTerminal: false, isActive: true, countsTowardAutoOpenLimit: true },
  { phaseName: "Acquisition",    colour: LegacyTokens.blue,       description: "Active teaching with prompt fading.",           entryRule: "From Baseline",      advancementRule: "≥80% correct across 3 sessions",                 regressionRule: "≤50% over 2 sessions → back to Baseline",   transitionsTo: "Generalization", masteredTargetStatus: "", notMasteredTransitionsTo: "", icon: "Layers",             minPercentCorrect: 80,  acrossSessions: 3, minTrialsPerSession: 5, promptRequirement: "any",              manualApprovalRequired: false, isStarting: false, isTerminal: false, isActive: true, countsTowardAutoOpenLimit: true },
  { phaseName: "Generalization", colour: LegacyTokens.amber,      description: "Same target across settings/materials/people.", entryRule: "From Acquisition",   advancementRule: "≥90% independent across 2 sessions",             regressionRule: "≤70% over 2 sessions → back to Acquisition",transitionsTo: "Mastered", masteredTargetStatus: "", notMasteredTransitionsTo: "",       icon: "AutoFixHigh",        minPercentCorrect: 90,  acrossSessions: 2, minTrialsPerSession: 5, promptRequirement: "independent_only", manualApprovalRequired: false, isStarting: false, isTerminal: false, isActive: true, countsTowardAutoOpenLimit: true },
  { phaseName: "Mastered",       colour: LegacyTokens.emerald,    description: "Target is met.",                                 entryRule: "From Generalization",advancementRule: "Automatic move to Maintenance on next session",  regressionRule: "",                                          transitionsTo: "Maintenance", masteredTargetStatus: "", notMasteredTransitionsTo: "",    icon: "EmojiEvents",        minPercentCorrect: 100, acrossSessions: 1, minTrialsPerSession: 1, promptRequirement: "independent_only", manualApprovalRequired: true,  isStarting: false, isTerminal: true,  isActive: true, countsTowardAutoOpenLimit: false },
  { phaseName: "Maintenance",    colour: LegacyTokens.purple,     description: "Periodic probes to keep the skill.",             entryRule: "From Mastered",      advancementRule: "N/A",                                            regressionRule: "≤80% → back to Acquisition",                transitionsTo: "", masteredTargetStatus: "", notMasteredTransitionsTo: "",               icon: "Repeat",             minPercentCorrect: 80,  acrossSessions: 1, minTrialsPerSession: 3, promptRequirement: "independent_only", manualApprovalRequired: false, isStarting: false, isTerminal: true,  isActive: true, countsTowardAutoOpenLimit: false },
];

export default function MasteryWorkflowEditor({ mode }: { mode: "create" | "view" | "edit" }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const preset = search.get("preset");

  // T63 Part J — objective-first creation. In CREATE mode we require
  // an ?objective=<key> query param (from ObjectiveChooserDialog). No
  // param → show the chooser and DO NOT render the editor body with a
  // silent "Increase Percent Correct" fallback. Legacy stored labels
  // on existing rows still resolve via objectiveByLabel below.
  const objectiveKeyParam = search.get("objective");
  const objectiveFromQuery: WorkflowObjective | null = objectiveByKey(objectiveKeyParam);
  const { showMessage, showErrorMessage } = useNotifier();
  const readOnly = mode === "view";

  const { data: fetched, isFetching: fetching } = useGetMasteryWorkflowByIdQuery(id!, { skip: !id });
  const [create, { isLoading: creating }] = useCreateMasteryWorkflowMutation();
  const [update, { isLoading: updating }] = useUpdateMasteryWorkflowMutation();

  const [workflowName, setWorkflowName] = React.useState("");
  // T63 Part J — no silent default. On CREATE the objective label is
  // seeded from the ?objective=<key> query catalogue entry; on
  // EDIT the effect below hydrates from the persisted row.
  const [objective, setObjective]       = React.useState<string>(
    mode === "create" && objectiveFromQuery ? objectiveFromQuery.label : "",
  );
  // T63 Part O — canonical slug carried into PhaseCard so it renders
  // Frequency criteria for Frequency workflows. Hydrated from
  // fetched.data.objective_key for edits; seeded from URL on create.
  const [objectiveKey, setObjectiveKey] = React.useState<string | null>(
    mode === "create" && objectiveFromQuery ? objectiveFromQuery.key : null,
  );
  // T63 Part O §19 / §L — new workflows initialise with the HiRasmus
  // Probe / Acquisition / Mastered skeleton (Waiting is rendered
  // separately by WorkflowEditorBody). Editor never shows a lone
  // "Unnamed phase" on the create route.
  const [phases, setPhases]             = React.useState<EditorPhase[]>(
    mode === "create" && objectiveFromQuery
      ? createStandardWorkflowForObjective(objectiveFromQuery.key)
      : [makeEmptyPhase(0)],
  );
  const [dirty, setDirty]               = React.useState(false);
  const [validated, setValidated]       = React.useState(false);

  // Batch 17 / 7C §7C.5 — auto-open workflow-scoped settings. Initial
  // phase is held as an index into `phases[]` so it survives the
  // "soft-delete + re-insert every phase on save" pattern the backend
  // uses; the controller resolves index → new phase FK after inserts.
  const [autoOpenEnabled, setAutoOpenEnabled] = React.useState(false);
  const [autoOpenMax, setAutoOpenMax]         = React.useState<number | null>(null);
  const [autoOpenInitIdx, setAutoOpenInitIdx] = React.useState<number | null>(null);
  const [autoOpenDir, setAutoOpenDir]         = React.useState<string | null>(null);

  const nameRef = React.useRef<HTMLInputElement | null>(null);

  // T63 Part J — when the chooser navigates from /new to
  // /new?objective=<key>, the Editor component stays mounted (same
  // route) and the `objective` useState initializer does NOT re-run,
  // so we would otherwise be stuck with an empty objective string.
  // Sync it here on every objectiveFromQuery change during create.
  React.useEffect(() => {
    if (mode === "create" && objectiveFromQuery && objective === "") {
      setObjective(objectiveFromQuery.label);
      setObjectiveKey(objectiveFromQuery.key);
      // Seed the standard skeleton on the same tick — the phases
      // useState initializer ran when objectiveFromQuery was still null
      // (chooser branch), so phases is currently a single empty phase.
      // Only replace if the operator hasn't touched anything yet.
      if (!dirty) {
        setPhases(createStandardWorkflowForObjective(objectiveFromQuery.key));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectiveKeyParam, mode]);

  React.useEffect(() => {
    if (fetched?.data) {
      const w: any = fetched.data;
      setWorkflowName(w.workflowName || "");
      // T63 Part J — persisted historical rows keep whatever label
      // they had on disk (backend column is free-text varchar). No
      // silent "Increase Percent Correct" fallback on empty.
      setObjective(w.objective || "");
      setObjectiveKey(w.objective_key || null);
      const ps = (w.phases || []).map((p: any, i: number) => normalisePhase(p, i));
      setPhases(ps.length ? ps : [makeEmptyPhase(0)]);
      // Resolve initial-phase FK back to an array index for the editor
      // form. If the FK is null OR points at a phase not in the active
      // set (soft-deleted, gone), the index is null and the FE surface
      // shows "unavailable" — see WaitingPhaseCard's initialGone note.
      setAutoOpenEnabled(!!w.autoOpenEnabled);
      setAutoOpenMax(w.autoOpenMaxSimultaneous != null ? Number(w.autoOpenMaxSimultaneous) : null);
      setAutoOpenDir(w.autoOpenDirection ?? null);
      const initIdx = w.autoOpenInitialPhaseId != null
        ? ps.findIndex((p: EditorPhase) => Number(p.id) === Number(w.autoOpenInitialPhaseId))
        : -1;
      setAutoOpenInitIdx(initIdx >= 0 ? initIdx : (w.autoOpenInitialPhaseId != null ? -1 : null));
      setDirty(false);
    } else if (preset === "standard" && mode === "create") {
      setWorkflowName("Standard Mastery Workflow");
      setPhases(STANDARD_PHASES.map((p, i) => normalisePhase({ ...p, orderNumber: i }, i)));
    }
  }, [fetched, preset, mode]);

  const workflowNameError = React.useMemo<string | null>(() => {
    if (!validated) return null;
    if (!workflowName.trim()) return "Please enter a name for this workflow.";
    return null;
  }, [workflowName, validated]);

  const anyPhaseAccuracyError = React.useMemo(() => {
    if (!validated) return false;
    return phases.some((p) => accuracyValidation(p).error !== null);
  }, [phases, validated]);

  const disableSave = validated && (Boolean(workflowNameError) || anyPhaseAccuracyError);

  const focusFirstInvalid = () => {
    if (!workflowName.trim()) {
      nameRef.current?.focus();
      nameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // If any phase accuracy is invalid, scroll to the first offending card.
    const firstBad = phases.findIndex((p) => accuracyValidation(p).error !== null);
    if (firstBad >= 0) {
      const cards = document.querySelectorAll<HTMLElement>('[data-testid="phase-card"]');
      cards[firstBad]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const save = async () => {
    setValidated(true);
    if (!workflowName.trim() || phases.some((p) => accuracyValidation(p).error !== null)) {
      setTimeout(focusFirstInvalid, 0);
      return;
    }
    // Six fields — status, isDefault, scope, automaticProgression,
    // manualOverrideAllowed, supervisorApprovalRequired — DELIBERATELY
    // absent from this payload. The editor has no UI for any of them, so
    // sending hardcoded values was silently clobbering row state on every
    // save (a locked/archived/supervisor-gated workflow reset to defaults
    // on any unrelated edit). Backend now preserves absent keys; FE sends
    // only what the editor actually captures. Adding a UI for any of
    // these means adding the corresponding key back here.
    const payload = {
      id: id ? Number(id) : undefined,
      workflowName: workflowName.trim(),
      objective,
      // T63 Part K — canonical slug from the WORKFLOW_OBJECTIVES
      // catalogue. Backend ignores it on update (objective is locked
      // after creation) but persists it on create alongside the free
      // text label so future evaluator code can branch on the family
      // without string-comparing labels.
      objective_key: mode === "create" ? (objectiveFromQuery?.key ?? null) : undefined,
      phases: phases.map((p, i) => ({
        // Batch 18 — send phase.id so the backend can preserve intervals
        // across the soft-delete-and-reinsert save shape. Undefined for
        // new phases the user just added; those have no intervals to
        // preserve.
        id: p.id,
        phaseName: p.phaseName.trim(),
        colour: p.colour,
        icon: p.icon,
        description: p.description || null,
        entryRule: p.entryRule || null,
        advancementRule: p.advancementRule || null,
        regressionRule: p.regressionRule || null,
        transitionsTo: p.transitionsTo || null,
        // Aug 2026 — "" from the select means "no explicit mastery
        // outcome" and must be sent as null so the backend keeps the
        // legacy advance-via-transitionsTo behaviour.
        masteredTargetStatus: (p.masteredTargetStatus && p.masteredTargetStatus.trim() !== "")
          ? p.masteredTargetStatus
          : null,
        // Aug 2026 Path B — send blank as null so legacy phases keep
        // the "stay in phase on failure" behaviour (backend treats
        // null as legacy). Only populated when the admin has picked
        // an explicit If-Not-Mastered Next Phase.
        notMasteredTransitionsTo: (p.notMasteredTransitionsTo && p.notMasteredTransitionsTo.trim() !== "")
          ? p.notMasteredTransitionsTo
          : null,
        minPercentCorrect: p.minPercentCorrect,
        acrossSessions: p.acrossSessions,
        minTrialsPerSession: p.minTrialsPerSession,
        promptRequirement: p.promptRequirement,
        manualApprovalRequired: p.manualApprovalRequired,
        isStarting: p.isStarting,
        isTerminal: p.isTerminal,
        isActive: p.isActive,
        countsTowardAutoOpenLimit: p.countsTowardAutoOpenLimit,
        // T63 Part O — HiRasmus Probe-behavior flags. Always sent so
        // an operator toggling OFF round-trips as `false`, not
        // "field-absent-means-preserve" (matches the existing pattern
        // for the other explicit booleans above).
        onlyProbeFirst: !!p.onlyProbeFirst,
        autoAddPhaseLineOnEnter: !!p.autoAddPhaseLineOnEnter,
        // T63 Part O — Frequency threshold. Backend derives the rest of
        // the criterion metadata (metricType / operator / unit /
        // aggregation) from the workflow's objective_key, so we only
        // send the numeric threshold here.
        thresholdValue: (typeof p.thresholdValue === "number" && Number.isFinite(p.thresholdValue))
          ? p.thresholdValue
          : null,
        orderNumber: i,
      })),
      // Batch 17 / 7C §7C.5. Send -1 as null so the "unavailable"
      // resolved state degrades cleanly on save (backend resolves an
      // out-of-range index to a null FK, disabling auto-open).
      autoOpenEnabled: autoOpenEnabled,
      autoOpenMaxSimultaneous: autoOpenMax,
      autoOpenInitialPhaseIndex: autoOpenInitIdx != null && autoOpenInitIdx >= 0 ? autoOpenInitIdx : null,
      autoOpenDirection: autoOpenDir,
    };
    try {
      const res: any = id
        ? await update(payload).unwrap()
        : await create(payload).unwrap();
      if (res?.status) {
        showMessage(res.message || "Saved");
        setDirty(false);
        navigate("/aba-therapy/settings/workflows");
      } else {
        showErrorMessage(res?.message || "Save failed");
      }
    } catch {
      showErrorMessage("Save failed");
    }
  };

  const busy = creating || updating;

  // T63 Part J — on the /new route without a valid objective, DO NOT
  // render the editor body. Show only the objective chooser; picking
  // an entry navigates to /new?objective=<key> and this component
  // re-renders with objectiveFromQuery set. Cancelling returns to the
  // list. This is the "no silent default" guard the ticket demands.
  if (mode === "create" && !objectiveFromQuery) {
    return (
      <ObjectiveChooserDialog
        open
        onClose={() => {
          navigate("/aba-therapy/settings/workflows");
        }}
        onSelect={(o) => {
          navigate(`/aba-therapy/settings/workflows/new?objective=${encodeURIComponent(o.key)}${
            preset ? `&preset=${encodeURIComponent(preset)}` : ""
          }`, { replace: true });
        }}
      />
    );
  }

  return (
    <Box sx={{ position: "relative", minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      {fetching && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: 12, color: LegacyTokens.ink3 }}>Loading workflow…</Typography>
        </Box>
      )}
      <WorkflowModal
        open
        title={
          mode === "create" ? "Add workflow"
            : mode === "view" ? (workflowName || "Workflow")
              : "Edit workflow"
        }
        onClose={() => navigate("/aba-therapy/settings/workflows")}
        onSave={save}
        saving={busy}
        disableSave={disableSave}
        hasUnsavedChanges={dirty}
      >
        <WorkflowEditorBody
          workflowName={workflowName}
          onWorkflowNameChange={(v) => { setDirty(true); setWorkflowName(v); }}
          workflowNameError={workflowNameError}
          objective={objective}
          objectiveKey={objectiveKey}
          phases={phases}
          onPhasesChange={(u) => { setDirty(true); setPhases(u); }}
          autoOpenEnabled={autoOpenEnabled}
          autoOpenMaxSimultaneous={autoOpenMax}
          autoOpenInitialPhaseIndex={autoOpenInitIdx}
          autoOpenDirection={autoOpenDir}
          onAutoOpenChange={(patch) => {
            setDirty(true);
            if (patch.autoOpenEnabled !== undefined) setAutoOpenEnabled(patch.autoOpenEnabled);
            if (patch.autoOpenMaxSimultaneous !== undefined) setAutoOpenMax(patch.autoOpenMaxSimultaneous);
            if (patch.autoOpenInitialPhaseIndex !== undefined) setAutoOpenInitIdx(patch.autoOpenInitialPhaseIndex);
            if (patch.autoOpenDirection !== undefined) setAutoOpenDir(patch.autoOpenDirection);
          }}
          registerNameRef={(el) => { nameRef.current = el; }}
          readOnly={readOnly}
        />
      </WorkflowModal>
    </Box>
  );
}
